import { useEffect, useRef } from 'react';

interface BackgroundMusicProps {
  /** The track URL stored on the proposal/template (in-built /audio/*.mp3
   *  or an AI-generated hosted URL). When `null` the component is inert. */
  url: string | null;
  /** When `true` the player pauses and unmounts its audio element. */
  isMuted: boolean;
  /** The viewer is showing actual slide content (i.e. past the curtain,
   *  consent, and intro). The player only starts in this phase so
   *  autoplay is triggered after a user gesture on the consent screen. */
  isPlaying: boolean;
  /** Master volume 0..1. Defaults to 0.55 so the music sits behind the
   *  slide voice-over in the cinematic experience. */
  volume?: number;
}

/**
 * Background music player for the proposal viewer.
 *
 * The viewer's `musicUrl` previously only ever played inside the music
 * picker (`MusicTrackTile`). Neither the admin's `ProposalPreview` nor
 * the public `ProposalPage` ever instantiated an `<audio>` element, so
 * the selected track — local in-built or AI-generated — was silent
 * when the deck actually played. This component fixes that:
 *
 *   - Mounts a hidden `<audio>` element bound to `url`.
 *   - Starts playback only when `isPlaying` flips to `true` (after the
 *     user has consented and the curtain has lifted), so it complies
 *     with the browser's autoplay policy.
 *   - Pauses + resets when `isPlaying` flips to `false`.
 *   - Honours the `isMuted` toggle by pausing the element without
 *     clearing the source, so unmuting resumes mid-track.
 *   - Restarts from the beginning whenever the URL changes (e.g. the
 *     user picks a different track in the wizard and the template is
 *     re-saved).
 */
export function BackgroundMusic({ url, isMuted, isPlaying, volume = 0.55 }: BackgroundMusicProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Keep a stable audio element; we only swap its `src` when the URL
  // changes so a re-render doesn't restart the track.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!audioRef.current) {
      const element = new Audio();
      element.preload = 'auto';
      element.crossOrigin = 'anonymous';
      audioRef.current = element;
    }
    const element = audioRef.current;
    element.loop = true;
    element.volume = Math.max(0, Math.min(1, volume));
    if (url && element.src !== url) {
      element.src = url;
      element.load();
    } else if (!url) {
      element.pause();
      element.removeAttribute('src');
      element.load();
    }
    return () => {
      // No teardown — the page-level audio context survives remounts.
    };
  }, [url, volume]);

  // Play / pause based on the viewer phase and the mute toggle.
  useEffect(() => {
    const element = audioRef.current;
    if (!element || !url) return;

    if (isPlaying && !isMuted) {
      // Modern browsers require a user gesture before audio can play.
      // The viewer always reaches the `playing` phase through a tap
      // (consent / begin), so this is the safe moment to call play().
      const playPromise = element.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Autoplay blocked or track 404'd. Silent — the rest of the
          // proposal still renders fine without music.
        });
      }
    } else {
      element.pause();
    }
  }, [isPlaying, isMuted, url]);

  // We render a visually hidden element so screen readers can still
  // discover the audio source (helps with ARIA conformance for the
  // "proposal has background music" announcement).
  return url ? <audio src={url} preload="auto" className="sr-only" aria-hidden /> : null;
}
